import { useState } from 'react';
import toast from 'react-hot-toast';

import { kioskApi } from '../../api/kiosk.js';
import { ApiError } from '../../api/client.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SEX_OPTIONS = [
  { value: 'unknown', label: 'Non so' },
  { value: 'male', label: 'Maschio' },
  { value: 'female', label: 'Femmina' }
];

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink">
      {label}
      {children}
    </label>
  );
}

const inputCls = 'rounded-lg border border-black/10 bg-white px-3 py-2 text-sm text-ink outline-none focus:border-accent';

/**
 * In-chat self-registration form, opened by the assistant's proposeRegistration
 * tool and pre-filled with what was discussed. The customer reviews the data,
 * gives the consents (explicit checkboxes) and submits directly to the kiosk
 * register endpoint. No password is ever shown or requested here: the server
 * emails a temporary one. The language model never sees this data.
 */
export function RegisterWidget({ prefill, customerSessionId }) {
  const p = prefill || {};
  const pet = p.pet || {};
  const [form, setForm] = useState({
    firstName: p.firstName || '',
    lastName: p.lastName || '',
    email: p.email || '',
    phone: p.phone || '',
    petName: pet.name || '',
    species: pet.species || '',
    breed: pet.breed || '',
    sex: SEX_OPTIONS.some((o) => o.value === pet.sex) ? pet.sex : 'unknown',
    weightKg: pet.weightKg != null ? String(pet.weightKg) : '',
    allergies: Array.isArray(pet.allergies) ? pet.allergies.join(', ') : '',
    notes: pet.notes || ''
  });
  const [consents, setConsents] = useState({ dataProcessing: false, photos: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key) => (e) => setConsents((c) => ({ ...c, [key]: e.target.checked }));

  const valid = form.firstName.trim() && EMAIL_RE.test(form.email.trim()) && form.petName.trim() && consents.dataProcessing;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim() || null,
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        pet: {
          name: form.petName.trim(),
          species: form.species.trim() || null,
          breed: form.breed.trim() || null,
          sex: form.sex,
          weightKg: form.weightKg ? Number(form.weightKg) : null,
          allergies: form.allergies.split(',').map((s) => s.trim()).filter(Boolean),
          notes: form.notes.trim() || null
        },
        consents: {
          dataProcessing: true,
          photos: consents.photos
        }
      };
      await kioskApi.register(body, { customerSessionId });
      setDone(true);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Registrazione non riuscita. Riprova.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-black/10 bg-white p-4 text-ink shadow-sm">
        <p className="text-sm font-semibold">Registrazione completata 🎉</p>
        <p className="mt-1 text-sm text-ink-muted">
          Ti abbiamo inviato un'email con le credenziali per accedere al tuo spazio personale.
          Al primo accesso ti verrà chiesto di scegliere una nuova password.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-black/10 bg-white p-4 text-ink shadow-sm">
      <p className="mb-3 text-sm font-semibold">Crea il tuo spazio personale</p>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Nome *"><input className={inputCls} value={form.firstName} onChange={set('firstName')} /></Field>
        <Field label="Cognome"><input className={inputCls} value={form.lastName} onChange={set('lastName')} /></Field>
        <Field label="Email *"><input className={inputCls} type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Telefono"><input className={inputCls} value={form.phone} onChange={set('phone')} /></Field>
      </div>

      <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-ink-muted">Il tuo animale</p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Nome *"><input className={inputCls} value={form.petName} onChange={set('petName')} /></Field>
        <Field label="Specie"><input className={inputCls} value={form.species} onChange={set('species')} placeholder="cane, gatto…" /></Field>
        <Field label="Razza"><input className={inputCls} value={form.breed} onChange={set('breed')} /></Field>
        <Field label="Sesso">
          <select className={inputCls} value={form.sex} onChange={set('sex')}>
            {SEX_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Peso (kg)"><input className={inputCls} type="number" min="0" step="0.1" value={form.weightKg} onChange={set('weightKg')} /></Field>
        <Field label="Allergie"><input className={inputCls} value={form.allergies} onChange={set('allergies')} placeholder="separane con virgola" /></Field>
      </div>
      <div className="mt-2">
        <Field label="Note alimentari o particolari"><textarea className={inputCls} rows={2} value={form.notes} onChange={set('notes')} /></Field>
      </div>

      <div className="mt-4 flex flex-col gap-2 text-xs text-ink">
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={consents.dataProcessing} onChange={toggle('dataProcessing')} className="mt-0.5" />
          <span>Acconsento al trattamento dei miei dati personali per la gestione del mio account e del mio animale. <strong>(obbligatorio)</strong></span>
        </label>
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={consents.photos} onChange={toggle('photos')} className="mt-0.5" />
          <span>Acconsento all'uso di foto del mio animale nel mio spazio personale.</span>
        </label>
      </div>

      <button
        type="submit"
        disabled={!valid || submitting}
        className="mt-4 w-full rounded-full bg-accent px-4 py-2.5 text-sm font-medium text-white transition hover:bg-accent/90 disabled:opacity-50"
      >
        {submitting ? 'Registro…' : 'Completa la registrazione'}
      </button>
    </form>
  );
}
